import type { Label } from './presets';
import { encodeWav, lcg } from './sfx';

/**
 * Music generated from code, rendered in the browser and uploaded like any other track (so it
 * gets the usual waveform), with its exact tempo for beat sync.
 *
 * Every seed is a different piece: key and mode, chord progression, drum groove variant, bass,
 * lead sounds, arpeggio and a hook. The hook is written the way people write them: a two-bar
 * rhythm repeated as A A A B, its notes taken from the chord under them (passing tones only on
 * weak steps), so it is catchy and never clashes. Chords are voice-led (each chord moves as
 * little as possible from the previous one).
 *
 * The sound: band-limited oscillators (PolyBLEP), zero-delay-feedback filters that stay clean up
 * to the top of the spectrum, 808-style metallic hats, a detuned stereo supersaw for pads, FM
 * keys and bells, a convolution reverb (decaying stereo noise, darker as it fades), a ping-pong
 * delay, sidechain pumping, then a master bus: low cut, glue compression, loudness to a target
 * and a look-ahead limiter. The arrangement follows the length of the video (intro, build-up,
 * drop, breakdown, second drop, a final chord) and lands crashes and impacts on the video's cuts.
 */

export const MUSIC_RATE = 44_100;

export const MUSIC_STYLES = [
  'pulse',
  'house',
  'synthpop',
  'drive',
  'dnb',
  'trap',
  'hiphop',
  'lofi',
  'funk',
  'corporate',
  'ambient',
  'cinematic',
  'tension',
] as const;
export type MusicStyle = (typeof MUSIC_STYLES)[number];

export const MUSIC_ENERGIES = ['calm', 'normal', 'high'] as const;
export type MusicEnergy = (typeof MUSIC_ENERGIES)[number];

const l = (en: string, uk: string, pl: string, ro: string, es: string, ru: string): Label => [
  en,
  uk,
  pl,
  ro,
  es,
  ru,
];

type Mode = 'minor' | 'major' | 'dorian';
type Bass = 'octave' | 'pump' | 'rolling' | 'sub808' | 'reese' | 'funk' | 'long' | 'walk';
type Lead = 'pluck' | 'saw' | 'square' | 'bell' | 'keys' | 'strings' | 'brass';

interface StyleDef {
  label: Label;
  /** Default tempo and the range a seed picks from. */
  bpm: number;
  range: [number, number];
  modes: Mode[];
  swing: number;
  /** Groove key in GROOVES. */
  groove: keyof typeof GROOVES;
  bass: Bass[];
  leads: Lead[];
  pad: boolean;
  arp: number;
  /** How much pads and bass duck under the kick. */
  pump: number;
  sevenths: boolean;
  /** Steps per chord: 16 = one chord a bar, 8 = two. */
  chordSteps: number[];
  /** Reverb length, seconds. */
  room: number;
  /** Tape and vinyl: a darker top and a little crackle. */
  dust?: boolean;
}

/** Drum grooves: 16 steps per bar; x = hit, o = soft (ghost) hit. Several variants each. */
const GROOVES = {
  four: {
    kick: ['x...x...x...x...', 'x...x...x...x..o', 'x...x...x..ox...'],
    snare: ['....x.......x...', '....x.......x..o', '....x..o....x...'],
    hat: ['..x...x...x...x.', 'o.x.o.x.o.x.o.x.', 'oox.oox.oox.oox.'],
  },
  pop: {
    kick: ['x..x..x.x.......', 'x......xx.......', 'x..x....x..x....'],
    snare: ['....x.......x...', '....x.......x.o.', '....x...o...x...'],
    hat: ['x.x.x.x.x.x.x.x.', 'xoxoxoxoxoxoxoxo', 'x.xox.xox.xox.xo'],
  },
  drive: {
    kick: ['x...x...x...x...', 'x.....x.x.......', 'x...x.....x.x...'],
    snare: ['....x.......x...', '....x.......x..x', '....x.......xo.o'],
    hat: ['xoxoxoxoxoxoxoxo', 'x.x.x.x.x.x.x.x.', 'xoxxxoxxxoxxxoxx'],
  },
  dnb: {
    kick: ['x.........x.....', 'x.........x..x..', 'x......x..x.....'],
    snare: ['....x.......x...', '....x..o....x.o.', '....x.......x..o'],
    hat: ['x.x.x.x.x.x.x.x.', 'xoxoxoxoxoxoxoxo', 'x.xox.xox.xox.xo'],
  },
  trap: {
    kick: ['x......x..x.....', 'x.....x...x..x..', 'x..x......x.....'],
    snare: ['........x.......', '........x......o', '........x...o...'],
    hat: ['x.x.x.x.x.x.x.x.', 'xxxxx.x.xxxxx.x.', 'x.x.xxx.x.x.xxxx'],
  },
  boombap: {
    kick: ['x......x..x.....', 'x.x....x..x.....', 'x......xx...x...'],
    snare: ['....x.......x...', '....x..o....x...', '....x.......x.o.'],
    hat: ['x.x.x.x.x.x.x.x.', 'x.xox.x.x.xox.x.', 'x.x.x.xxx.x.x.x.'],
  },
  funk: {
    kick: ['x..x..x...x..x..', 'x.x...x...x..x..', 'x..x...x..x...x.'],
    snare: ['....x..o.o..x...', '.o..x..o....x.o.', '....x.o.....x..o'],
    hat: ['xoxoxoxoxoxoxoxo', 'xoxxxoxxxoxxxoxx', 'x.xxx.xxx.xxx.xx'],
  },
  epic: {
    kick: ['x.......x.......', 'x.....x.x.......', 'x..x....x.......'],
    snare: ['................', '............x...', '........x.......'],
    hat: ['x...x...x...x...', 'x.x.x.x.x.x.x.x.', '................'],
  },
  none: { kick: ['................'], snare: ['................'], hat: ['................'] },
};

export const MUSIC_STYLE_INFO: Record<MusicStyle, StyleDef> = {
  pulse: {
    label: l(
      'Electro pulse',
      'Електро-пульс',
      'Elektro puls',
      'Puls electro',
      'Pulso electro',
      'Электро-пульс',
    ),
    bpm: 122,
    range: [118, 128],
    modes: ['minor', 'dorian'],
    swing: 0,
    groove: 'four',
    bass: ['octave', 'pump'],
    leads: ['pluck', 'saw', 'bell'],
    pad: true,
    arp: 0.9,
    pump: 0.65,
    sevenths: false,
    chordSteps: [16],
    room: 1.8,
  },
  house: {
    label: l('House', 'Хаус', 'House', 'House', 'House', 'Хаус'),
    bpm: 124,
    range: [120, 128],
    modes: ['minor', 'dorian', 'major'],
    swing: 0.08,
    groove: 'four',
    bass: ['pump', 'funk'],
    leads: ['keys', 'pluck', 'bell'],
    pad: true,
    arp: 0.4,
    pump: 0.75,
    sevenths: true,
    chordSteps: [16, 8],
    room: 1.6,
  },
  synthpop: {
    label: l('Synth-pop', 'Синт-поп', 'Synth-pop', 'Synth-pop', 'Synth-pop', 'Синт-поп'),
    bpm: 112,
    range: [104, 120],
    modes: ['major', 'minor'],
    swing: 0,
    groove: 'pop',
    bass: ['octave', 'rolling'],
    leads: ['square', 'saw', 'bell'],
    pad: true,
    arp: 0.6,
    pump: 0.35,
    sevenths: false,
    chordSteps: [16, 8],
    room: 1.8,
  },
  drive: {
    label: l(
      'Synthwave drive',
      'Синтвейв-драйв',
      'Synthwave',
      'Synthwave',
      'Synthwave',
      'Синтвейв-драйв',
    ),
    bpm: 104,
    range: [96, 112],
    modes: ['minor'],
    swing: 0,
    groove: 'drive',
    bass: ['rolling'],
    leads: ['saw', 'brass', 'square'],
    pad: true,
    arp: 0.7,
    pump: 0.3,
    sevenths: false,
    chordSteps: [16],
    room: 2.4,
  },
  dnb: {
    label: l(
      'Drum & bass',
      'Драм-н-бейс',
      'Drum & bass',
      'Drum & bass',
      'Drum & bass',
      'Драм-н-бейс',
    ),
    bpm: 172,
    range: [168, 176],
    modes: ['minor'],
    swing: 0,
    groove: 'dnb',
    bass: ['reese'],
    leads: ['bell', 'pluck', 'strings'],
    pad: true,
    arp: 0.3,
    pump: 0.25,
    sevenths: true,
    chordSteps: [16],
    room: 2,
  },
  trap: {
    label: l('Trap', 'Треп', 'Trap', 'Trap', 'Trap', 'Трэп'),
    bpm: 140,
    range: [130, 150],
    modes: ['minor'],
    swing: 0,
    groove: 'trap',
    bass: ['sub808'],
    leads: ['bell', 'pluck', 'strings'],
    pad: false,
    arp: 0.3,
    pump: 0,
    sevenths: false,
    chordSteps: [16],
    room: 1.6,
  },
  hiphop: {
    label: l(
      'Hip-hop beat',
      'Хіп-хоп біт',
      'Bit hip-hop',
      'Beat hip-hop',
      'Beat hip-hop',
      'Хип-хоп бит',
    ),
    bpm: 92,
    range: [86, 98],
    modes: ['minor', 'dorian'],
    swing: 0.16,
    groove: 'boombap',
    bass: ['long', 'sub808'],
    leads: ['keys', 'bell'],
    pad: false,
    arp: 0,
    pump: 0,
    sevenths: true,
    chordSteps: [16],
    room: 1.2,
    dust: true,
  },
  lofi: {
    label: l('Lo-fi chill', 'Lo-fi чіл', 'Lo-fi chill', 'Lo-fi chill', 'Lo-fi chill', 'Lo-fi чилл'),
    bpm: 80,
    range: [72, 88],
    modes: ['major', 'dorian'],
    swing: 0.2,
    groove: 'boombap',
    bass: ['long', 'walk'],
    leads: ['keys', 'pluck'],
    pad: false,
    arp: 0.2,
    pump: 0,
    sevenths: true,
    chordSteps: [16, 8],
    room: 1.4,
    dust: true,
  },
  funk: {
    label: l('Funky groove', 'Фанк-грув', 'Funk', 'Groove funk', 'Groove funk', 'Фанк-грув'),
    bpm: 108,
    range: [100, 116],
    modes: ['dorian', 'minor'],
    swing: 0.1,
    groove: 'funk',
    bass: ['funk', 'walk'],
    leads: ['keys', 'brass', 'square'],
    pad: false,
    arp: 0,
    pump: 0,
    sevenths: true,
    chordSteps: [16, 8],
    room: 1.1,
  },
  corporate: {
    label: l(
      'Upbeat corporate',
      'Бадьорий корпоратив',
      'Pozytywny korporacyjny',
      'Corporate optimist',
      'Corporativo animado',
      'Бодрый корпоратив',
    ),
    bpm: 116,
    range: [108, 124],
    modes: ['major'],
    swing: 0,
    groove: 'pop',
    bass: ['octave', 'rolling'],
    leads: ['pluck', 'bell', 'keys'],
    pad: true,
    arp: 0.9,
    pump: 0.25,
    sevenths: false,
    chordSteps: [16, 8],
    room: 1.5,
  },
  ambient: {
    label: l('Ambient pads', 'Ембієнт', 'Ambient', 'Ambient', 'Ambient', 'Эмбиент'),
    bpm: 72,
    range: [64, 84],
    modes: ['major', 'dorian', 'minor'],
    swing: 0,
    groove: 'none',
    bass: ['long'],
    leads: ['bell', 'pluck'],
    pad: true,
    arp: 0.3,
    pump: 0,
    sevenths: true,
    chordSteps: [16],
    room: 3.4,
  },
  cinematic: {
    label: l(
      'Cinematic epic',
      'Кінематографічний епік',
      'Epicki filmowy',
      'Epic cinematic',
      'Épico cinematográfico',
      'Кинематографичный эпик',
    ),
    bpm: 90,
    range: [84, 100],
    modes: ['minor'],
    swing: 0,
    groove: 'epic',
    bass: ['long'],
    leads: ['strings', 'brass'],
    pad: true,
    arp: 0.5,
    pump: 0,
    sevenths: false,
    chordSteps: [16],
    room: 3,
  },
  tension: {
    label: l(
      'Trailer tension',
      'Трейлерна напруга',
      'Napięcie zwiastuna',
      'Tensiune de trailer',
      'Tensión de tráiler',
      'Трейлерное напряжение',
    ),
    bpm: 96,
    range: [90, 104],
    modes: ['minor'],
    swing: 0,
    groove: 'epic',
    bass: ['long', 'reese'],
    leads: ['strings', 'bell'],
    pad: true,
    arp: 0.6,
    pump: 0,
    sevenths: false,
    chordSteps: [16],
    room: 2.8,
  },
};

export interface MusicOptions {
  style: MusicStyle;
  /** Length, ms (the music ends with the video). */
  duration: number;
  bpm?: number;
  seed?: number;
  rate?: number;
  energy?: MusicEnergy;
  /** Cuts of the video, ms: crashes and impacts land exactly on them. */
  hits?: number[];
}

export interface GeneratedMusic {
  /** Interleaved stereo. */
  samples: Float32Array;
  rate: number;
  channels: 2;
  bpm: number;
  /** What the seed chose, for the UI ("A minor · 124 BPM"). */
  summary: string;
}

const midi = (n: number) => 440 * Math.pow(2, (n - 69) / 12);
const TAU = Math.PI * 2;
const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const SCALES: Record<Mode, number[]> = {
  minor: [0, 2, 3, 5, 7, 8, 10],
  major: [0, 2, 4, 5, 7, 9, 11],
  dorian: [0, 2, 3, 5, 7, 9, 10],
};
/** Progressions as scale degrees (0 = tonic). */
const PROGRESSIONS: Record<Mode, number[][]> = {
  minor: [
    [0, 5, 2, 6],
    [0, 3, 5, 4],
    [0, 6, 5, 6],
    [0, 4, 5, 3],
    [5, 6, 0, 0],
    [0, 3, 6, 2],
    [0, 5, 3, 4],
  ],
  major: [
    [0, 4, 5, 3],
    [5, 3, 0, 4],
    [0, 3, 4, 3],
    [0, 5, 3, 4],
    [3, 4, 2, 5],
    [0, 2, 3, 4],
  ],
  dorian: [
    [0, 3, 0, 3],
    [0, 6, 3, 0],
    [0, 4, 3, 6],
    [0, 1, 6, 3],
  ],
};

/** Hook rhythms, two bars of 16th steps: x = a note, - = it holds, . = rest. */
const HOOKS = {
  dance: [
    'x--x--x-x---x---x--x--x-x-------',
    '..x-..x-..x-x---..x-..x-x-x-----',
    'x-x-..x-x-x-..x-x-x-..x-x-------',
    'x--x--x---x-x---x--x--x---x-----',
  ],
  pop: [
    'x---x-x-x---x---x---x-x-x-------',
    'x-x-x-x-x---x---x-x-x-x-x-------',
    '..x-x-x-x---x-x-..x-x-x-x-------',
  ],
  chill: [
    'x-------x---x---x-------x-------',
    'x---x-------x---x---x---x-------',
    '....x-x-x-------....x-x-x-------',
  ],
  epic: [
    'x-------x-------x-------x-------',
    'x-----x-x-------x-----x-x-------',
    'x---------x-----x---------------',
  ],
};

type Section = 'intro' | 'build' | 'drop' | 'break' | 'outro';

/** Sections bar by bar for a piece of `bars` bars. */
function arrangement(bars: number, rnd: () => number): Section[] {
  const out: Section[] = Array.from({ length: bars }, () => 'drop');
  if (bars <= 3) {
    out[0] = 'intro';
    return out;
  }
  const intro = bars >= 12 ? 2 : 1;
  for (let i = 0; i < intro; i++) out[i] = 'intro';
  out[intro] = 'build';
  if (bars >= 12) {
    // A breakdown and a second build-up in the middle
    const mid = Math.round(bars * (0.5 + rnd() * 0.12));
    const len = bars >= 20 ? 2 : 1;
    for (let i = mid; i < Math.min(bars - 3, mid + len); i++) out[i] = 'break';
    if (mid + len < bars - 2) out[mid + len] = 'build';
  }
  if (bars >= 6) out[bars - 1] = 'outro';
  return out;
}

// ---------------------------------------------------------------- DSP

/**
 * Zero-delay-feedback state-variable filter (Simper / Zavalishin): stable and in tune up to the
 * top of the spectrum, unlike the Chamberlin one. `lp`, `bp`, `hp` hold the last outputs.
 */
class Svf {
  lp = 0;
  bp = 0;
  hp = 0;
  private s1 = 0;
  private s2 = 0;
  private k = 1.41;
  private a1 = 0;
  private a2 = 0;
  private a3 = 0;
  private readonly rate: number;
  constructor(rate: number, cutoff = 1000, q = 0.707) {
    this.rate = rate;
    this.set(cutoff, q);
  }
  set(cutoff: number, q = 0.707) {
    const fc = Math.min(Math.max(cutoff, 16), this.rate * 0.45);
    const g = Math.tan((Math.PI * fc) / this.rate);
    this.k = 1 / q;
    this.a1 = 1 / (1 + g * (g + this.k));
    this.a2 = g * this.a1;
    this.a3 = g * this.a2;
  }
  tick(x: number): number {
    const v3 = x - this.s2;
    const v1 = this.a1 * this.s1 + this.a2 * v3;
    const v2 = this.s2 + this.a2 * this.s1 + this.a3 * v3;
    this.s1 = 2 * v1 - this.s1;
    this.s2 = 2 * v2 - this.s2;
    this.lp = v2;
    this.bp = v1;
    this.hp = x - this.k * v1 - v2;
    return v2;
  }
}

/** PolyBLEP residual: removes the aliasing of a saw / square jump. */
function blep(t: number, dt: number): number {
  if (t < dt) {
    t /= dt;
    return t + t - t * t - 1;
  }
  if (t > 1 - dt) {
    t = (t - 1) / dt;
    return t * t + t + t + 1;
  }
  return 0;
}

/** Band-limited oscillator; `dt` = frequency / rate. */
class Osc {
  p: number;
  constructor(phase = 0) {
    this.p = phase;
  }
  saw(dt: number): number {
    const v = 2 * this.p - 1 - blep(this.p, dt);
    this.p += dt;
    if (this.p >= 1) this.p -= 1;
    return v;
  }
  square(dt: number): number {
    let v = this.p < 0.5 ? 1 : -1;
    v += blep(this.p, dt);
    v -= blep((this.p + 0.5) % 1, dt);
    this.p += dt;
    if (this.p >= 1) this.p -= 1;
    return v;
  }
}

/** In-place radix-2 complex FFT. */
function fft(re: Float64Array, im: Float64Array, inverse: boolean) {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      let t = re[i];
      re[i] = re[j];
      re[j] = t;
      t = im[i];
      im[i] = im[j];
      im[j] = t;
    }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const half = len >> 1;
    const ang = ((inverse ? 2 : -2) * Math.PI) / len;
    const wr = Math.cos(ang);
    const wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let cr = 1;
      let ci = 0;
      for (let j = 0; j < half; j++) {
        const a = i + j;
        const b = a + half;
        const xr = re[b] * cr - im[b] * ci;
        const xi = re[b] * ci + im[b] * cr;
        re[b] = re[a] - xr;
        im[b] = im[a] - xi;
        re[a] += xr;
        im[a] += xi;
        const t = cr * wr - ci * wi;
        ci = cr * wi + ci * wr;
        cr = t;
      }
    }
  }
  if (inverse)
    for (let i = 0; i < n; i++) {
      re[i] /= n;
      im[i] /= n;
    }
}

/**
 * A mono signal through a stereo impulse response, by overlap-add FFT convolution. Both sides in
 * one pass: the left IR goes in the real part and the right one in the imaginary part.
 */
function convolveStereo(
  x: Float32Array,
  irL: Float32Array,
  irR: Float32Array,
): [Float32Array, Float32Array] {
  const m = irL.length;
  let size = 1;
  while (size < m * 2) size <<= 1;
  const block = size - m + 1;
  const hr = new Float64Array(size);
  const hi = new Float64Array(size);
  hr.set(irL);
  hi.set(irR);
  fft(hr, hi, false);
  const outL = new Float32Array(x.length);
  const outR = new Float32Array(x.length);
  const re = new Float64Array(size);
  const im = new Float64Array(size);
  for (let s = 0; s < x.length; s += block) {
    re.fill(0);
    im.fill(0);
    const end = Math.min(x.length, s + block);
    let any = false;
    for (let i = s; i < end; i++) {
      re[i - s] = x[i];
      if (x[i] !== 0) any = true;
    }
    if (!any) continue;
    fft(re, im, false);
    for (let k = 0; k < size; k++) {
      const a = re[k];
      const b = im[k];
      re[k] = a * hr[k] - b * hi[k];
      im[k] = a * hi[k] + b * hr[k];
    }
    fft(re, im, true);
    const lim = Math.min(size, x.length - s);
    for (let i = 0; i < lim; i++) {
      outL[s + i] += re[i];
      outR[s + i] += im[i];
    }
  }
  return [outL, outR];
}

/** Stereo room: decorrelated noise that decays by 60 dB over `seconds` and darkens as it fades. */
function reverbIR(rate: number, seconds: number, rnd: () => number): [Float32Array, Float32Array] {
  const m = Math.round(rate * seconds);
  const pre = Math.round(rate * 0.016);
  const sides: Float32Array[] = [];
  for (let c = 0; c < 2; c++) {
    const ir = new Float32Array(m);
    let lp = 0;
    let energy = 0;
    for (let i = pre; i < m; i++) {
      const t = (i - pre) / rate;
      const a = 0.85 * Math.exp((-t * 3) / seconds) + 0.06; // damping: the tail loses its top
      lp += a * (rnd() * 2 - 1 - lp);
      const v = lp * Math.exp((-6.9 * t) / seconds) * Math.min(1, t / 0.01);
      ir[i] = v;
      energy += v * v;
    }
    // A few early reflections
    for (let k = 0; k < 6; k++) {
      const at = pre + Math.round(rate * (0.004 + rnd() * 0.05));
      if (at < m) ir[at] += (rnd() < 0.5 ? -1 : 1) * 0.25 * Math.sqrt(energy / 2000);
    }
    const g = 1 / Math.sqrt(energy + 1e-9);
    for (let i = 0; i < m; i++) ir[i] *= g;
    sides.push(ir);
  }
  return [sides[0], sides[1]];
}

// ---------------------------------------------------------------- the piece

export function generateMusic(o: MusicOptions): GeneratedMusic {
  const rate = o.rate ?? MUSIC_RATE;
  const def = MUSIC_STYLE_INFO[o.style];
  const rnd = lcg(((o.seed ?? 1) * 92821 + MUSIC_STYLES.indexOf(o.style) * 7919) >>> 0);
  const pick = <T>(list: readonly T[]): T => list[Math.floor(rnd() * list.length)];
  const bpm = Math.round(
    Math.max(50, Math.min(200, o.bpm ?? def.range[0] + rnd() * (def.range[1] - def.range[0]))),
  );
  const energy = o.energy ?? 'normal';
  const E = energy === 'calm' ? 0.6 : energy === 'high' ? 1.25 : 1;
  const seconds = Math.max(2, Math.min(300, o.duration / 1000));
  const n = Math.round(seconds * rate);
  // Buses: dry, sidechained (ducks under the kick), reverb and delay sends
  const L = new Float32Array(n);
  const R = new Float32Array(n);
  const DL = new Float32Array(n);
  const DR = new Float32Array(n);
  const REV = new Float32Array(n);
  const DLY = new Float32Array(n);

  const beat = 60 / bpm;
  const step = beat / 4;
  const bar = beat * 4;
  const bars = Math.max(1, Math.ceil(seconds / bar));
  const mode = pick(def.modes);
  const scale = SCALES[mode];
  const root = 36 + Math.floor(rnd() * 12); // C2..B2
  const prog = pick(PROGRESSIONS[mode]);
  const chordSteps = pick(def.chordSteps);
  const swing = def.swing;
  const sections = arrangement(bars, rnd);
  const g = GROOVES[def.groove];
  const groove = { kick: pick(g.kick), snare: pick(g.snare), hat: pick(g.hat) };
  const bassKind = pick(def.bass);
  const lead = pick(def.leads);
  const lead2 = pick(def.leads);
  const halfTime = def.groove === 'trap';
  const kicks: number[] = [];
  const drums = def.groove !== 'none';
  const acoustic = def.groove === 'boombap' || def.groove === 'funk';

  /** Pitch of scale degree `d` (any integer) above `base`. */
  const degree = (d: number, base = root) => {
    const oct = Math.floor(d / 7);
    return base + scale[((d % 7) + 7) % 7] + 12 * oct;
  };
  const chordAt = (stepIndex: number) => {
    const k = Math.floor(stepIndex / chordSteps) % prog.length;
    const d = prog[k];
    const tones = [0, 2, 4, ...(def.sevenths ? [6] : [])].map((x) => d + x);
    return { d, tones, pcs: tones.map((x) => degree(x) % 12) };
  };
  const sec = (t: number) => Math.round(t * rate);
  const noise = () => rnd() * 2 - 1;
  const att = (t: number, a: number) => (t < a ? t / a : 1);
  const rel = (t: number, len: number, r: number) => Math.min(1, Math.max(0, (len - t) / r));

  // ---------------------------------------------------------------- mixing
  interface Bus {
    duck?: boolean;
    rev?: number;
    dly?: number;
  }
  const put = (t0: number, src: Float32Array, gain: number, pan: number, bus: Bus = {}) => {
    const a = sec(t0);
    if (a >= n) return;
    const gl = gain * Math.cos(((pan + 1) * Math.PI) / 4) * Math.SQRT2;
    const gr = gain * Math.sin(((pan + 1) * Math.PI) / 4) * Math.SQRT2;
    const tl = bus.duck ? DL : L;
    const tr = bus.duck ? DR : R;
    const rv = gain * (bus.rev ?? 0);
    const dl = gain * (bus.dly ?? 0);
    const from = Math.max(0, -a);
    const to = Math.min(src.length, n - a);
    for (let i = from; i < to; i++) {
      const v = src[i];
      tl[a + i] += v * gl;
      tr[a + i] += v * gr;
      if (rv) REV[a + i] += v * rv;
      if (dl) DLY[a + i] += v * dl;
    }
  };
  const putStereo = (t0: number, sl: Float32Array, sr: Float32Array, gain: number, bus: Bus) => {
    const a = sec(t0);
    if (a >= n) return;
    const tl = bus.duck ? DL : L;
    const tr = bus.duck ? DR : R;
    const rv = gain * 0.5 * (bus.rev ?? 0);
    const to = Math.min(sl.length, n - a);
    for (let i = Math.max(0, -a); i < to; i++) {
      tl[a + i] += sl[i] * gain;
      tr[a + i] += sr[i] * gain;
      if (rv) REV[a + i] += (sl[i] + sr[i]) * rv;
    }
  };

  // ---------------------------------------------------------------- drum kit (rendered once)
  const render = (len: number, fn: (t: number, i: number) => number) => {
    const out = new Float32Array(sec(len));
    for (let i = 0; i < out.length; i++) out[i] = fn(i / rate, i);
    return out;
  };
  /** 808 cymbal: six detuned square waves, band-passed, plus a little noise. */
  const metal = (len: number, decay: number, cut: number, seedShift: number) => {
    const freqs = [263, 400, 421, 474, 587, 845].map((f) => f * (1.6 + seedShift * 0.03));
    const ph = freqs.map(() => rnd());
    const bp = new Svf(rate, 10_000, 0.9);
    const hp = new Svf(rate, cut, 0.7);
    return render(len, (t) => {
      let s = 0;
      for (let k = 0; k < 6; k++) {
        ph[k] += freqs[k] / rate;
        if (ph[k] >= 1) ph[k] -= 1;
        s += ph[k] < 0.5 ? 1 : -1;
      }
      const nz = noise();
      bp.tick(s / 6 + nz * 0.5);
      // Metal for the colour, plain air on top for the sizzle
      hp.tick(bp.bp + nz * 0.35);
      return hp.hp * Math.exp(-t * decay) * att(t, 0.0005);
    });
  };
  /** The kick is tuned to the key: the root's octave between 41 and 62 Hz. */
  let kickTune = midi((root % 12) + 24);
  while (kickTune < 41) kickTune *= 2;
  while (kickTune >= 62) kickTune /= 2;
  const kit = (() => {
    const deep = def.groove === 'trap' || def.groove === 'epic';
    const kick = (() => {
      const f0 = acoustic ? 110 : 150;
      const f1 = kickTune;
      const decay = deep ? 3.2 : acoustic ? 9 : 6.5;
      const click = new Svf(rate, 3500, 0.8);
      let ph = 0;
      return render(deep ? 1 : 0.5, (t) => {
        ph += (f1 + (f0 - f1) * Math.exp(-t * 32)) / rate;
        const body = Math.sin(TAU * ph) * Math.exp(-t * decay);
        const tick = click.tick(noise()) * Math.exp(-t * 400) * (acoustic ? 0.6 : 0.35);
        return Math.tanh((body + tick) * 1.7) * att(t, 0.0015);
      });
    })();
    const snare = (() => {
      const bp = new Svf(rate, 1900, 0.7);
      const hp = new Svf(rate, 4500, 0.7);
      return render(0.35, (t) => {
        const nz = noise();
        bp.tick(nz);
        hp.tick(nz);
        const body =
          Math.sin(TAU * 185 * t) * Math.exp(-t * 28) +
          Math.sin(TAU * 330 * t) * Math.exp(-t * 40) * 0.5;
        const wires = bp.bp * Math.exp(-t * 16) * 1.4 + hp.hp * Math.exp(-t * 22) * 0.6;
        return Math.tanh((body * 0.8 + wires) * 1.3) * att(t, 0.001);
      });
    })();
    const clap = (() => {
      const bp = new Svf(rate, 1300, 1.4);
      const hp = new Svf(rate, 3000, 0.7);
      return render(0.4, (t) => {
        const burst =
          t < 0.03
            ? Math.exp(-((t % 0.0105) / 0.0105) * 4) * (t < 0.021 ? 0.8 : 1)
            : Math.exp(-(t - 0.03) * 13);
        const nz = noise();
        bp.tick(nz);
        hp.tick(nz);
        return (bp.bp * 2 + hp.hp * 0.5) * burst;
      });
    })();
    const hats = [0, 1, 2, 3].map((k) => metal(0.08, 62 + k * 6, 7500, k));
    const open = metal(0.45, 9, 6800, 2);
    const crash = metal(2.6, 1.7, 4500, 5);
    const impact = (() => {
      const lp = new Svf(rate, 260, 0.8);
      let ph = 0;
      return render(2, (t) => {
        ph += (34 + 80 * Math.exp(-t * 7)) / rate;
        const sub = Math.sin(TAU * ph) * Math.exp(-t * 2);
        return Math.tanh((sub + lp.tick(noise()) * Math.exp(-t * 3.5) * 2.2) * 1.4);
      });
    })();
    /** Noise that swells into a hit (reverse-cymbal feel). */
    const swell = (() => {
      const bp = new Svf(rate, 800, 0.8);
      const len = 0.42;
      return render(len, (t, i) => {
        const k = t / len;
        if ((i & 31) === 0) bp.set(600 + 7000 * k * k, 0.9);
        bp.tick(noise());
        return bp.bp * Math.pow(k, 3) * 1.6;
      });
    })();
    const toms = [60, 75, 95, 120].map((p) =>
      render(0.7, (t) =>
        Math.tanh(
          Math.sin(TAU * (p + p * 0.7 * Math.exp(-t * 14)) * t) * Math.exp(-t * 4.5) * 1.6 +
            noise() * Math.exp(-t * 60) * 0.15,
        ),
      ),
    );
    const norm = (b: Float32Array) => {
      let p = 0;
      for (const v of b) p = Math.max(p, Math.abs(v));
      if (p > 0) for (let i = 0; i < b.length; i++) b[i] /= p;
      return b;
    };
    [kick, snare, clap, ...hats, open, crash, impact, swell, ...toms].forEach(norm);
    return { kick, snare, clap, hats, open, crash, impact, swell, toms };
  })();
  let hatIx = 0;

  const kick = (t0: number, v: number) => {
    kicks.push(t0);
    put(t0, kit.kick, v * 0.85, 0);
  };
  const snare = (t0: number, v: number, clap: boolean) =>
    put(t0, clap ? kit.clap : kit.snare, v * (clap ? 0.55 : 0.5), clap ? 0.04 : -0.04, {
      rev: 0.32,
    });
  const hat = (t0: number, v: number, open: boolean, pan: number) =>
    put(t0, open ? kit.open : kit.hats[hatIx++ % 4], v * (open ? 0.28 : 0.42), pan, {
      rev: open ? 0.08 : 0.03,
    });
  const crash = (t0: number, v: number) => put(t0, kit.crash, v * 0.3, 0.15, { rev: 0.35 });
  const impact = (t0: number, v: number) => {
    put(t0, kit.impact, v * 0.85, 0, { rev: 0.4 });
    put(t0 - 0.42, kit.swell, v * 0.25, -0.1, { rev: 0.3 });
  };
  const riser = (t0: number, len: number, v: number) => {
    const bp = new Svf(rate, 400, 0.8);
    const o = new Osc();
    const lp = new Svf(rate, 400, 1);
    put(
      t0,
      render(len, (t, i) => {
        const k = t / len;
        if ((i & 31) === 0) {
          bp.set(300 + 8000 * k * k, 1);
          lp.set(300 + 3500 * k, 1.2);
        }
        bp.tick(noise());
        const tone = lp.tick(o.saw((110 * Math.pow(2, 3 * k)) / rate)) * 0.35;
        return (bp.bp * 1.4 + tone) * k * k;
      }),
      v,
      0,
      { rev: 0.35 },
    );
  };

  // ---------------------------------------------------------------- instruments
  /** Plucked synth: saw + square through a filter that closes fast. */
  const pluck = (note: number, len: number, bright = 1) => {
    const f = midi(note);
    const dt = f / rate;
    const dur = Math.min(len + 0.3, 1);
    const a = new Osc(rnd());
    const b = new Osc(rnd());
    const flt = new Svf(rate);
    return render(dur, (t, i) => {
      if ((i & 15) === 0) flt.set(f * 1.2 + 300 + 7000 * bright * Math.exp(-t * 11), 0.95);
      const x = a.saw(dt) * 0.6 + b.square(dt * 1.004) * 0.4;
      return flt.tick(x) * att(t, 0.002) * Math.exp(-t * 5) * rel(t, dur, 0.04);
    });
  };
  /** FM bell. */
  const bell = (note: number, len: number) => {
    const f = midi(note);
    const dur = Math.max(len, 1.4);
    return render(dur, (t) => {
      const idx = 2.4 * Math.exp(-t * 4);
      return (
        (Math.sin(TAU * f * t + idx * Math.sin(TAU * f * 3.5 * t)) +
          0.25 * Math.sin(TAU * f * 2 * t) * Math.exp(-t * 6)) *
        Math.exp(-t * 2.3) *
        att(t, 0.002) *
        rel(t, dur, 0.1)
      );
    });
  };
  /** FM electric piano: bell-like tine on the attack, warm body after. */
  const keys = (note: number, len: number) => {
    const f = midi(note);
    const dur = len + 0.35;
    return render(dur, (t) => {
      const idx = 1.6 * Math.exp(-t * 7) + 0.25;
      const body = Math.sin(TAU * f * t + idx * Math.sin(TAU * f * t));
      const tine = Math.sin(TAU * f * 7 * t) * Math.exp(-t * 35) * 0.12;
      return (
        (body + tine) *
        Math.exp(-t * 1.3) *
        (1 + 0.1 * Math.sin(TAU * 4.5 * t)) *
        att(t, 0.003) *
        rel(t, dur, 0.3)
      );
    });
  };
  /** Mono lead: square, saw (two detuned) or brass (filter opening on the attack). */
  const leadVoice = (kind: 'square' | 'saw' | 'brass', note: number, len: number) => {
    const f = midi(note);
    const dur = len + 0.12;
    const a = new Osc(rnd());
    const b = new Osc(rnd());
    const flt = new Svf(rate);
    const vib = 5 + rnd();
    return render(dur, (t, i) => {
      const v = 1 + 0.005 * Math.sin(TAU * vib * t) * Math.min(1, Math.max(0, t - 0.15) * 4);
      const dt = (f * v) / rate;
      let x: number;
      let cut: number;
      if (kind === 'square') {
        x = a.square(dt) * 0.7;
        cut = 1600 + 2400 * Math.exp(-t * 7);
      } else if (kind === 'saw') {
        x = (a.saw(dt * 0.996) + b.saw(dt * 1.004)) * 0.45;
        cut = 2400 + 2200 * Math.exp(-t * 5);
      } else {
        x = (a.saw(dt) + b.saw(dt * 1.003)) * 0.45;
        cut = 500 + 2800 * (1 - Math.exp(-t * 14)) * (0.6 + 0.4 * Math.exp(-t * 2));
      }
      if ((i & 15) === 0) flt.set(cut, 0.8);
      return flt.tick(x) * att(t, kind === 'brass' ? 0.04 : 0.006) * rel(t, dur, 0.1);
    });
  };
  /**
   * Supersaw chord in stereo: every note is five detuned saws spread left and right, through a
   * low-pass. Pads (slow attack) and strings (faster, brighter, with vibrato).
   */
  const ensemble = (notes: number[], len: number, kind: 'pad' | 'strings', bright: number) => {
    const attack = kind === 'pad' ? 0.35 : 0.12;
    const dur = len + (kind === 'pad' ? 0.6 : 0.35);
    const N = sec(dur);
    const outL = new Float32Array(N);
    const outR = new Float32Array(N);
    const det = [-0.011, -0.005, 0, 0.006, 0.012];
    const pans = [-1, -0.5, 0, 0.5, 1];
    const voices = notes.flatMap((nn) =>
      det.map((d, k) => ({ dt: (midi(nn) * (1 + d)) / rate, o: new Osc(rnd()), pan: pans[k] })),
    );
    const fl = new Svf(rate);
    const fr = new Svf(rate);
    const norm = 0.55 / Math.sqrt(voices.length);
    const vibRate = 4.8 + rnd();
    for (let i = 0; i < N; i++) {
      const t = i / rate;
      const vib = kind === 'strings' ? 1 + 0.0025 * Math.sin(TAU * vibRate * t) : 1;
      let l = 0;
      let r = 0;
      for (const v of voices) {
        const s = v.o.saw(v.dt * vib);
        l += s * (1 - v.pan) * 0.5;
        r += s * (1 + v.pan) * 0.5;
      }
      if ((i & 31) === 0) {
        const cut =
          (kind === 'pad' ? 1500 : 2600) * bright * (1 + 0.25 * Math.sin(TAU * 0.15 * t)) +
          (kind === 'strings' ? 900 * Math.min(1, t / 0.4) : 0);
        fl.set(cut, 0.7);
        fr.set(cut, 0.7);
      }
      const e = Math.min(1, t / attack) * rel(t, dur, kind === 'pad' ? 0.6 : 0.35) * norm;
      outL[i] = fl.tick(l) * e;
      outR[i] = fr.tick(r) * e;
    }
    return { l: outL, r: outR };
  };

  let lastBassHz = 0;
  const bassNote = (t0: number, note: number, len: number, v: number) => {
    const f = midi(note);
    const dt = f / rate;
    const dur = len;
    const a = new Osc();
    const b = new Osc(0.25);
    const flt = new Svf(rate);
    let ph = 0;
    let src: Float32Array;
    switch (bassKind) {
      case 'sub808': {
        const from = lastBassHz && Math.abs(Math.log2(lastBassHz / f)) < 1.01 ? lastBassHz : f;
        src = render(dur + 0.05, (t) => {
          const hz = f + (from - f) * Math.exp(-t * 40);
          ph += hz / rate;
          return (
            Math.tanh(Math.sin(TAU * ph) * 2.2) *
            0.75 *
            att(t, 0.003) *
            Math.exp(-t * 0.9) *
            rel(t, dur + 0.05, 0.06)
          );
        });
        v *= 1.25;
        break;
      }
      case 'reese':
        src = render(dur, (t, i) => {
          if ((i & 31) === 0) flt.set(320 + 220 * Math.sin(TAU * 0.45 * (t0 + t)), 0.9);
          const x = (a.saw(dt * 0.994) + b.saw(dt * 1.006)) * 0.5;
          return (
            (flt.tick(x) * 1.3 + Math.sin(TAU * f * t) * 0.5) * att(t, 0.01) * rel(t, dur, 0.05)
          );
        });
        break;
      case 'rolling':
      case 'funk':
        src = render(dur, (t, i) => {
          if ((i & 15) === 0)
            flt.set(
              bassKind === 'funk' ? 220 + 2000 * Math.exp(-t * 11) : 260 + 1300 * Math.exp(-t * 13),
              bassKind === 'funk' ? 2.2 : 1.1,
            );
          const x = a.saw(dt) * 0.7 + b.square(dt) * 0.3;
          return (
            (flt.tick(x) * 1.2 + Math.sin(TAU * f * t) * 0.45) * att(t, 0.003) * rel(t, dur, 0.03)
          );
        });
        break;
      case 'long':
      case 'walk':
        flt.set(bassKind === 'walk' ? 900 : 520, 0.7);
        src = render(dur, (t) => {
          const x = Math.sin(TAU * f * t) + 0.3 * Math.sin(TAU * f * 2 * t) + 0.1 * a.saw(dt);
          const decay = bassKind === 'walk' ? Math.exp(-t * 3) : 1;
          return (
            flt.tick(x) *
            1.1 *
            decay *
            att(t, bassKind === 'walk' ? 0.004 : 0.03) *
            rel(t, dur, 0.1)
          );
        });
        break;
      default:
        // Punchy synth bass (the one from the reel): sine with a few harmonics, driven
        src = render(dur, (t) => {
          const s =
            Math.sin(TAU * f * t) +
            0.35 * Math.sin(TAU * 2 * f * t) +
            0.12 * Math.sin(TAU * 3 * f * t);
          return Math.tanh(s * 1.4) * att(t, 0.004) * Math.exp(-t * 3.5) * rel(t, dur, 0.03);
        });
    }
    lastBassHz = f;
    put(t0, src, v, 0, { duck: true });
  };

  // ---------------------------------------------------------------- harmony: voice leading
  let prevVoicing: number[] = [];
  /** The chord's notes in [lo, hi], in the inversion closest to the previous chord. */
  const voice = (pcs: number[], lo: number, hi: number, prev: number[]): number[] => {
    let best: number[] = [];
    let bestCost = Infinity;
    for (let inv = 0; inv < pcs.length; inv++) {
      const order = [...pcs.slice(inv), ...pcs.slice(0, inv)];
      for (let base = lo - 12; base <= hi; base += 12) {
        const notes: number[] = [];
        let last = -Infinity;
        for (const pc of order) {
          let nn = base + ((((pc - base) % 12) + 12) % 12);
          while (nn <= last) nn += 12;
          notes.push(nn);
          last = nn;
        }
        if (notes[0] < lo || notes[notes.length - 1] > hi + 4) continue;
        const centre = (lo + hi) / 2;
        const cost = prev.length
          ? notes.reduce((s, x, i) => s + Math.abs(x - (prev[i] ?? prev[prev.length - 1])), 0)
          : Math.abs(notes[0] - centre + 4);
        if (cost < bestCost) {
          bestCost = cost;
          best = notes;
        }
      }
    }
    return best.length ? best : pcs.map((pc) => lo + pc);
  };
  /** Chord tones from `lo` to `hi` (the hook picks its notes from these). */
  const ladder = (pcs: number[], lo: number, hi: number) => {
    const out: number[] = [];
    for (let nn = lo; nn <= hi; nn++) if (pcs.includes(nn % 12)) out.push(nn);
    return out;
  };
  const scaleNotes = (lo: number, hi: number) => {
    const out: number[] = [];
    for (let nn = lo; nn <= hi; nn++)
      if (scale.includes((((nn - root) % 12) + 12) % 12)) out.push(nn);
    return out;
  };

  // ---------------------------------------------------------------- the hook
  const family =
    def.groove === 'four' || def.groove === 'drive' || def.groove === 'dnb'
      ? 'dance'
      : def.groove === 'pop' || def.groove === 'funk'
        ? 'pop'
        : def.groove === 'epic' || def.groove === 'none'
          ? 'epic'
          : 'chill';
  interface HookNote {
    s: number;
    len: number;
    idx: number;
    passing: boolean;
  }
  const parseHook = (pattern: string): HookNote[] => {
    const out: HookNote[] = [];
    let walk = 2 + Math.floor(rnd() * 2);
    for (let s = 0; s < pattern.length; s++) {
      if (pattern[s] !== 'x') continue;
      let len = 1;
      while (pattern[s + len] === '-') len++;
      // Mostly steps; sometimes a leap up, then it comes back down
      const r = rnd();
      walk += r < 0.35 ? 1 : r < 0.65 ? -1 : r < 0.8 ? 0 : r < 0.9 ? 2 : -2;
      walk = Math.max(0, Math.min(5, walk));
      out.push({ s, len, idx: walk, passing: s % 4 !== 0 && len === 1 && rnd() < 0.35 });
    }
    return out;
  };
  const hookA = parseHook(pick(HOOKS[family]));
  const hookB = (() => {
    // Same first bar, a new second bar that comes home (long note on a low chord tone)
    const second = parseHook(pick(HOOKS[family])).filter((x) => x.s >= 16);
    const out = [...hookA.filter((x) => x.s < 16), ...second];
    const last = out[out.length - 1];
    if (last && last.s >= 16) {
      last.idx = 0;
      last.passing = false;
      last.len = Math.max(last.len, 32 - last.s);
    }
    return out;
  })();
  const hook = (barIndex: number, octave: number, gain: number, kind: Lead, sparse = false) => {
    const phrase = Math.floor(barIndex / 2) % 4 === 3 ? hookB : hookA;
    const half = barIndex % 2;
    const lo = 64 + octave * 12;
    const notes = phrase.filter((x) => Math.floor(x.s / 16) === half && (!sparse || x.s % 8 === 0));
    notes.forEach((x, k) => {
      const stepInBar = x.s % 16;
      const abs = barIndex * 16 + stepInBar;
      const tones = ladder(chordAt(abs).pcs, lo, lo + 17);
      let note = tones[Math.min(x.idx, tones.length - 1)];
      if (x.passing) {
        // A scale step next to the chord tone, leading to the next note
        const sc = scaleNotes(lo - 2, lo + 19);
        const at = sc.indexOf(note);
        if (at > 0) note = sc[at + (k % 2 ? 1 : -1)] ?? note;
      }
      const t0 = barIndex * bar + stepInBar * step + (stepInBar % 2 ? swing * step : 0);
      const len = x.len * step * 0.92;
      const pan = k % 2 ? 0.15 : -0.15;
      const accent = stepInBar % 4 === 0 ? 1 : 0.8;
      voiceLead(kind, t0, note, len, gain * accent, pan);
    });
  };
  const voiceLead = (kind: Lead, t0: number, note: number, len: number, v: number, pan: number) => {
    switch (kind) {
      case 'pluck':
        put(t0, pluck(note, len, 1.1), v * 1.2, pan, { rev: 0.25, dly: 0.3 });
        break;
      case 'bell':
        put(t0, bell(note, len), v * 0.8, pan, { rev: 0.35, dly: 0.3 });
        break;
      case 'keys':
        put(t0, keys(note, len), v, pan, { rev: 0.3, dly: 0.15 });
        break;
      case 'strings': {
        const s = ensemble([note], len, 'strings', 1.1);
        putStereo(t0, s.l, s.r, v * 1.5, { rev: 0.45 });
        break;
      }
      default:
        put(t0, leadVoice(kind, note, len), v, pan, { rev: 0.25, dly: 0.2 });
    }
  };

  // ---------------------------------------------------------------- arrangement
  const hitsBars = new Set<number>();
  const arpPattern = pick(['reel', 'up', 'updown', 'down'] as const);
  const arpRate = pick([1, 2, 2, 1]); // steps per note
  const ARP_REEL = [0, 1, 2, 3, 2, 1, 3, 2];
  const padBright = 0.85 + rnd() * 0.4;
  for (let b = 0; b < bars; b++) {
    const t = b * bar;
    const secn = sections[b];
    const prevSec = sections[b - 1];
    const lastOfPhrase = b % 4 === 3 || sections[b + 1] === 'build' || sections[b + 1] === 'break';
    const full = secn === 'drop';
    const intro = secn === 'intro';
    const brk = secn === 'break';
    const build = secn === 'build';
    const outro = secn === 'outro';
    const sw = (s: number) => t + s * step + (s % 2 ? swing * step : 0);

    // A crash and an impact on the first beat of every drop
    if (full && prevSec !== 'drop') {
      crash(t, 0.9 * E);
      if (b > 0) impact(t, 0.55 * E);
      hitsBars.add(b);
    }

    // Drums
    if (drums && !brk) {
      for (let s = 0; s < 16; s++) {
        const k = groove.kick[s];
        const sn = groove.snare[s];
        const h = groove.hat[s];
        const fill = lastOfPhrase && full && s >= 12;
        if (outro) {
          if (s === 0) kick(t, 1);
          continue;
        }
        // Intro: half-note kicks only in its second bar, to lead in
        const introKick = intro && s % 8 === 0 && b > 0 && sections[b - 1] === 'intro';
        if (k !== '.' && (full || introKick || (build && s < 8)))
          kick(sw(s), (k === 'x' ? 1 : 0.55) * Math.min(1, E));
        if (!fill && sn !== '.' && (full || (build && s < 8)) && def.groove !== 'epic')
          snare(sw(s), sn === 'x' ? 1 : 0.3, def.groove === 'four' || def.groove === 'pop');
        if (h !== '.' && (full || intro || build)) {
          const open = def.groove === 'four' && s % 4 === 2 && h === 'x' && rnd() < 0.6;
          hat(sw(s), (h === 'x' ? 1 : 0.45) * (intro ? 0.6 : 1) * E, open, s % 2 ? 0.3 : -0.25);
        }
        // Trap hats roll in 32nds now and then
        if (def.groove === 'trap' && full && h !== '.' && rnd() < 0.12)
          for (let r = 1; r < 3; r++) hat(sw(s) + (r * step) / 3, 0.5, false, 0.35);
        if (fill) {
          if (def.groove === 'epic')
            put(sw(s), kit.toms[(s - 12) % 4], 0.6, -0.2 + (s - 12) * 0.15, { rev: 0.35 });
          else snare(sw(s), 0.35 + (s - 12) * 0.14, false);
          if (s === 15) snare(sw(s) + step / 2, 0.6, false);
        }
      }
      // Epic grooves: taiko-like toms instead of snares
      if (def.groove === 'epic' && (full || build)) {
        put(t, kit.toms[0], 0.85, -0.1, { rev: 0.35 });
        put(t + beat * 2.5, kit.toms[1], 0.6, 0.15, { rev: 0.35 });
        if (full && rnd() < 0.6) put(t + beat * 3.5, kit.toms[2], 0.5, 0.25, { rev: 0.35 });
      }
    }
    // Build-ups: a snare roll that speeds up and a riser
    if (build) {
      riser(t, bar, 0.32 * E);
      if (drums)
        for (let s = 8; s < 16; s += s < 12 ? 2 : 1)
          snare(t + s * step, 0.2 + (s - 8) * 0.08, false);
    }

    // Harmony, bass, arpeggio
    for (let s = 0; s < 16; s += chordSteps) {
      const c = chordAt(b * 16 + s);
      const len = outro ? bar : chordSteps * step;
      const voicing = voice(c.pcs, 55, 72, prevVoicing);
      prevVoicing = voicing;
      if (def.pad) {
        const p = ensemble(voicing, len, 'pad', padBright * (brk ? 0.8 : 1));
        putStereo(t + s * step, p.l, p.r, (brk || intro ? 0.8 : 0.6) * Math.min(1.2, E), {
          duck: true,
          rev: 0.5,
        });
      }
      if (!def.pad || outro) {
        // Comped chords on keys: on the beat, sometimes pushed
        const hits = outro ? [0] : def.groove === 'funk' ? [0, 3, 6, 10] : [0];
        for (const hs of hits)
          if (hs < chordSteps)
            for (const nn of voicing)
              put(
                t + (s + hs) * step,
                keys(nn, outro ? bar : step * (def.groove === 'funk' ? 1.5 : chordSteps * 0.9)),
                0.1,
                nn % 2 ? 0.25 : -0.25,
                { rev: 0.3 },
              );
      }
      // Bass
      const bn = degree(c.d, root - 12 + (root < 40 ? 12 : 0));
      if (outro) {
        if (s === 0) bassNote(t, degree(0, root - 12 + (root < 40 ? 12 : 0)), bar * 0.9, 0.5);
      } else if ((!brk && !intro) || (intro && (bassKind === 'long' || bassKind === 'sub808'))) {
        const v = 0.42 * Math.min(1.1, E);
        switch (bassKind) {
          case 'octave':
            for (let q = 0; q < chordSteps; q += 2)
              bassNote(sw(s + q), bn + (q % 4 === 2 ? 12 : 0), step * 1.6, v);
            break;
          case 'pump':
            for (let q = 2; q < chordSteps; q += 4)
              bassNote(sw(s + q), bn + (q % 8 === 6 ? 12 : 0), step * 1.8, v);
            break;
          case 'rolling':
            for (let q = 0; q < chordSteps; q++)
              bassNote(t + (s + q) * step, bn + (q % 8 === 7 ? 7 : 0), step * 0.9, v * 0.8);
            break;
          case 'sub808':
            // The 808 follows the kick pattern and glides between notes
            for (let q = 0; q < chordSteps; q++)
              if (groove.kick[(s + q) % 16] !== '.')
                bassNote(sw(s + q), bn + (rnd() < 0.15 ? 12 : 0), step * (halfTime ? 4 : 3), v);
            break;
          case 'reese':
            bassNote(t + s * step, bn, chordSteps * step, v);
            break;
          case 'funk': {
            const pattern = [0, 3, 6, 8, 10, 11, 14];
            for (const q of pattern)
              if (q < chordSteps)
                bassNote(
                  sw(s + q),
                  bn + (q === 6 ? 12 : q === 11 ? 10 : q === 14 ? 7 : 0),
                  step * 1.2,
                  v * (q === 0 ? 1 : 0.75),
                );
            break;
          }
          case 'walk':
            for (let q = 0; q < chordSteps; q += 4)
              bassNote(sw(s + q), bn + [0, 7, 12, 10][(q / 4) % 4], beat * 0.9, v);
            break;
          default:
            bassNote(t + s * step, bn, chordSteps * step * 0.98, v);
        }
      }
      // Arpeggio: chord tones an octave above the pad
      if (def.arp > 0 && !outro && (full || brk || build || (intro && def.arp > 0.6))) {
        const tones = voicing.map((x) => x + 12);
        const order =
          arpPattern === 'reel'
            ? ARP_REEL.map((i) => tones[i % tones.length])
            : arpPattern === 'up'
              ? tones
              : arpPattern === 'down'
                ? [...tones].reverse()
                : [...tones, ...[...tones].reverse().slice(1, -1)];
        for (let q = 0, i = 0; q < chordSteps; q += arpRate, i++) {
          const nn = order[i % order.length] + (q >= 8 && arpRate === 1 ? 12 : 0);
          put(
            t + (s + q) * step,
            pluck(nn, step * arpRate, 0.9),
            (0.2 * def.arp + 0.08) * E,
            i % 2 ? 0.6 : -0.6,
            {
              duck: true,
              dly: 0.3,
              rev: 0.25,
            },
          );
        }
      }
    }
    // The hook: in drops (the second drop an octave up, doubled); a hint in breaks
    const drops = sections
      .slice(0, b + 1)
      .filter((x, i, arr) => x === 'drop' && arr[i - 1] !== 'drop').length;
    const leadGain = 0.24 * Math.min(1.1, E);
    if (full)
      hook(b, drops >= 2 && lead2 !== 'strings' ? 1 : 0, leadGain, drops >= 2 ? lead2 : lead);
    if (full && drops >= 2 && lead2 !== lead) hook(b, 0, leadGain * 0.5, lead);
    if (brk) hook(b, 0, leadGain * 0.7, 'bell', true);
    if (outro) put(t, bell(degree(0, root + 36), bar), 0.12, 0.2, { rev: 0.6, dly: 0.3 });
  }

  // Cuts of the video: a crash and an impact exactly there
  for (const ms of o.hits ?? []) {
    const t = ms / 1000;
    if (t <= 0.05 || t >= seconds - 0.2) continue;
    const b = Math.floor(t / bar);
    if (hitsBars.has(b) && Math.abs(t - b * bar) < 0.15) continue;
    crash(t, 0.5 * E);
    impact(t, 0.4 * E);
  }

  // ---------------------------------------------------------------- mix
  // Sidechain: the duck bus breathes with the kick
  const pump = def.pump * Math.min(1.2, E);
  if (pump > 0) {
    const gain = new Float32Array(n).fill(1);
    const len = sec(beat * 0.85);
    const a0 = sec(0.004);
    for (const k of kicks) {
      const a = sec(k);
      for (let i = 0; i < len && a + i < n; i++) {
        const dip = i < a0 ? i / a0 : Math.pow(1 - (i - a0) / (len - a0), 2);
        gain[a + i] = Math.min(gain[a + i], 1 - pump * dip);
      }
    }
    for (let i = 0; i < n; i++) {
      DL[i] *= gain[i];
      DR[i] *= gain[i];
    }
  }
  for (let i = 0; i < n; i++) {
    L[i] += DL[i];
    R[i] += DR[i];
  }
  // Reverb (low cut on the send, so it never muddies the bass)
  const revHp = new Svf(rate, 220, 0.7);
  for (let i = 0; i < n; i++) {
    revHp.tick(REV[i]);
    REV[i] = revHp.hp;
  }
  const [irL, irR] = reverbIR(rate, def.room, rnd);
  const [rl, rr] = convolveStereo(REV, irL, irR);
  // Ping-pong delay, dotted eighths, darker each repeat
  const dt = sec(step * 3);
  const dlL = new Float32Array(n);
  const dlR = new Float32Array(n);
  const fbL = new Svf(rate, 3200, 0.6);
  const fbR = new Svf(rate, 3200, 0.6);
  for (let i = 0; i < n; i++) {
    const inL = i >= dt ? DLY[i - dt] + fbR.tick(dlR[i - dt]) * 0.42 : 0;
    const inR = i >= dt ? fbL.tick(dlL[i - dt]) * 0.42 : 0;
    dlL[i] = inL;
    dlR[i] = inR;
  }
  // Crackle and hiss for the tape styles
  if (def.dust) {
    const hiss = new Svf(rate, 5000, 0.6);
    for (let i = 0; i < n; i++) {
      const h = hiss.tick(noise()) * 0.006;
      const pop = rnd() < 6 / rate ? noise() * 0.25 : 0;
      L[i] += h + pop;
      R[i] += h - pop * 0.5;
    }
  }

  // Master: low cut, intro filter sweep, tape top for dusty styles, glue, loudness, limiter
  const out = new Float32Array(n * 2);
  const hpL = new Svf(rate, 28, 0.7);
  const hpR = new Svf(rate, 28, 0.7);
  const swL = new Svf(rate, 20_000, 0.75);
  const swR = new Svf(rate, 20_000, 0.75);
  const tpL = new Svf(rate, def.dust ? 9000 : 20_000, 0.6);
  const tpR = new Svf(rate, def.dust ? 9000 : 20_000, 0.6);
  const introEnd =
    Math.max(
      0,
      sections.findIndex((x) => x !== 'intro'),
    ) * bar;
  const wet = 0.5;
  let env = 0;
  const thr = 0.18;
  const atk = Math.exp(-1 / (rate * 0.012));
  const relc = Math.exp(-1 / (rate * 0.15));
  for (let i = 0; i < n; i++) {
    const t = i / rate;
    hpL.tick(L[i] + rl[i] * wet + dlL[i] * 0.5);
    hpR.tick(R[i] + rr[i] * wet + dlR[i] * 0.5);
    let l = hpL.hp;
    let r = hpR.hp;
    if (introEnd > 0 && t < introEnd) {
      if ((i & 31) === 0) {
        const cut = 350 + 16_000 * Math.pow(t / introEnd, 2.2);
        swL.set(cut, 0.9);
        swR.set(cut, 0.9);
      }
      l = swL.tick(l);
      r = swR.tick(r);
    }
    if (def.dust) {
      l = tpL.tick(l);
      r = tpR.tick(r);
    }
    // Glue: gentle 2:1 above the threshold, smooth attack and release
    const lvl = Math.max(Math.abs(l), Math.abs(r));
    env = lvl > env ? atk * env + (1 - atk) * lvl : relc * env + (1 - relc) * lvl;
    const gr = env > thr ? Math.pow(thr / env, 0.5) : 1;
    out[i * 2] = l * gr;
    out[i * 2 + 1] = r * gr;
  }
  // Loudness to a target (RMS of the louder half of the piece), then a look-ahead limiter
  {
    const win = sec(0.4);
    const rms: number[] = [];
    for (let a = 0; a + win <= n; a += win) {
      let s = 0;
      for (let i = a; i < a + win; i++) s += out[i * 2] ** 2 + out[i * 2 + 1] ** 2;
      rms.push(Math.sqrt(s / (2 * win)));
    }
    rms.sort((x, y) => y - x);
    const loud = rms.slice(0, Math.max(1, Math.ceil(rms.length / 2)));
    const level = loud.reduce((s, x) => s + x, 0) / loud.length;
    const target = Math.pow(10, (def.groove === 'none' ? -13 : -10.5) / 20);
    const makeup = level > 1e-6 ? Math.min(8, target / level) : 1;
    for (let i = 0; i < out.length; i++) out[i] *= makeup;
  }
  {
    const ceil = 0.89;
    const blk = 64;
    const blocks = Math.ceil(n / blk);
    const need = new Float32Array(blocks + 2).fill(1);
    for (let k = 0; k < blocks; k++) {
      let p = 0;
      for (let i = k * blk; i < Math.min(n, (k + 1) * blk); i++)
        p = Math.max(p, Math.abs(out[i * 2]), Math.abs(out[i * 2 + 1]));
      need[k] = p > ceil ? ceil / p : 1;
    }
    let gcur = 1;
    const relLim = Math.exp(-blk / (rate * 0.08));
    const fadeOut = Math.min(1.5, seconds * 0.1);
    for (let k = 0; k < blocks; k++) {
      // Look one block ahead: the gain is already down when the peak arrives
      const target = Math.min(need[k], need[k + 1]);
      const gnext = target < gcur ? target : relLim * gcur + (1 - relLim) * target;
      for (let i = k * blk; i < Math.min(n, (k + 1) * blk); i++) {
        const x = (i - k * blk) / blk;
        const gg = gcur + (gnext - gcur) * x;
        const t = i / rate;
        const fade = Math.min(1, t / 0.01, (seconds - t) / fadeOut);
        for (let c = 0; c < 2; c++) {
          const v = Math.tanh((out[i * 2 + c] * gg) / 0.95) * 0.95 * fade;
          out[i * 2 + c] = Math.max(-ceil, Math.min(ceil, v));
        }
      }
      gcur = gnext;
    }
  }

  const key = `${NOTE_NAMES[root % 12]} ${mode}`;
  return { samples: out, rate, channels: 2, bpm, summary: `${key} · ${bpm} BPM` };
}

/** The generated music as a stereo WAV file. */
export function musicWav(o: MusicOptions): { wav: Uint8Array; bpm: number; summary: string } {
  const { samples, rate, bpm, summary } = generateMusic(o);
  return { wav: encodeWav(samples, rate, 2), bpm, summary };
}
